import { getContractCustomersOverview } from '@/modules/Contract/services/customer'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import React, { useMemo } from 'react'
import ContractCard, { ContractCardSkeleton } from '../ContractCard'
import { CustomerContract } from '@/modules/Contract/types'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface ContractOverviewAllCardProps {
    encodedFilters?: string
}

const CONTRACT_LABELS = [
    'pending',
    'exchanging',
    'expiried',
    'uploading',
    'active',
]
type ContractGroupKey = (typeof CONTRACT_LABELS)[number]

const groupContractsByStatus = (contracts: CustomerContract[] = []) => {
    const groups: Record<ContractGroupKey, CustomerContract[]> = {
        pending: [],
        exchanging: [],
        uploading: [],
        active: [],
        expiried: [],
    }

    const today = new Date()

    for (const contract of contracts) {
        const isExpired =
            contract?.contract_status === 'active' &&
            contract?.contract_expiry_date &&
            new Date(contract?.contract_expiry_date) < today

        if (isExpired) {
            groups?.expiried?.push(contract)
        } else if (
            CONTRACT_LABELS.includes(
                contract?.contract_status as ContractGroupKey
            )
        ) {
            groups[contract?.contract_status as ContractGroupKey].push(contract)
        }
    }

    return groups
}

const ContractOverviewAllCardSkeleton = () => {
    return (
        <div className="flex gap-4 overflow-x-auto mt-4 bg-white p-4 rounded-lg">
            {Array.from({ length: 5 }).map((_, index) => (
                <div
                    key={index}
                    className="min-w-[500px] max-w-full w-[calc((100%-96px)/4)] shrink-0"
                >
                    {/* Header Skeleton */}
                    <div className="flex items-center gap-2 mb-2 animate-pulse">
                        <div className="h-4 w-24 bg-gray-300 rounded" />
                        <div className="h-5 w-6 bg-[#E6F0FF] rounded" />
                    </div>
                    <div className="flex flex-col gap-3">
                        {[1, 2, 3]?.map((i) => (
                            <ContractCardSkeleton key={i} />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    )
}

const ContractOverviewAllCard: React.FC<ContractOverviewAllCardProps> = ({
    encodedFilters,
}) => {
    const { t } = useTranslation()

    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CONTRACTS_CUSTOMERS_OVERVIEW, encodedFilters],
        queryFn: async () =>
            getContractCustomersOverview({
                filters: encodedFilters,
                q: '',
            }),
        loadingComponent: <ContractOverviewAllCardSkeleton />,
    })

    const finalContracts = useMemo(() => {
        return groupContractsByStatus(data?.results || [])
    }, [data?.results])
    const contractGroups = CONTRACT_LABELS.map((label) => ({
        label,
        contracts: finalContracts[label] || [],
    }))

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="flex gap-4 overflow-x-auto bg-white p-4 rounded-lg">
            {contractGroups.map((group) => (
                <div
                    key={group.label}
                    className="min-w-[500px] max-w-full w-[calc((100%-96px)/4)] shrink-0"
                >
                    <div className="flex items-center gap-2 mb-4">
                        <Typogaphy className="font-semibold text-md">
                            {t(`contract-${group.label}`)}
                        </Typogaphy>
                        <div className="bg-[#E6F0FF] px-2 py-0.5 rounded">
                            <Typogaphy className="text-primary text-xs font-medium">
                                {group?.contracts.length}
                            </Typogaphy>
                        </div>
                    </div>
                    <div className="flex flex-col gap-3">
                        {group?.contracts?.length ? (
                            group?.contracts
                                ?.slice(0, 3)
                                ?.map((contract) => (
                                    <ContractCard
                                        key={contract?.contract_id}
                                        contract={contract}
                                    />
                                ))
                        ) : (
                            <div className="h-[172px] w-full border rounded-lg" />
                        )}
                    </div>
                </div>
            ))}
        </div>
    )
}

export default ContractOverviewAllCard
